import { z } from 'zod';

/**
 * Pets: Tamagotchi-style companions that follow their owner around rooms,
 * need feeding, play, baths and sleep, grow up, learn tricks, and dig for
 * treasure. Kid-friendly: a neglected pet gets sad, and eventually sick, but
 * never dies or runs away.
 *
 * The server is authoritative over every stat. The pure functions here
 * (decay, mood, level, stage) are shared so the client can show the same
 * numbers between updates. Owned by the pets feature; every WebSocket type
 * starts with `pet:`.
 */

/* -------------------------------------------------------------------------- */
/* Species                                                                    */
/* -------------------------------------------------------------------------- */

export const PET_SPECIES = ['cat', 'dog', 'parrot', 'rabbit', 'hamster', 'tortoise', 'otter'] as const;
export type PetSpecies = (typeof PET_SPECIES)[number];

export const PET_FOODS = ['kibble', 'fish', 'seeds', 'carrot'] as const;
export type PetFood = (typeof PET_FOODS)[number];

export interface PetSpeciesInfo {
  label: string;
  description: string;
  /** Adoption price in Kaki Dollars. */
  price: number;
  variants: ReadonlyArray<{ id: string; label: string }>;
  /** Gives a little extra fun when eaten. */
  favouriteFood: PetFood;
}

export const PET_SPECIES_INFO: Readonly<Record<PetSpecies, PetSpeciesInfo>> = Object.freeze({
  cat: {
    label: 'Cat',
    description: 'Independent, curious, and very good at napping in sunny spots.',
    price: 800,
    favouriteFood: 'fish',
    variants: [
      { id: 'tabby', label: 'Tabby' },
      { id: 'black', label: 'Black' },
      { id: 'white', label: 'White' },
      { id: 'ginger', label: 'Kopitiam Ginger' },
    ],
  },
  dog: {
    label: 'Dog',
    description: 'Loyal, loves walks, and never says no to a game of fetch.',
    price: 900,
    favouriteFood: 'kibble',
    variants: [
      { id: 'corgi', label: 'Corgi' },
      { id: 'shiba', label: 'Shiba' },
      { id: 'singapore_special', label: 'Singapore Special' },
      { id: 'poodle', label: 'Poodle' },
    ],
  },
  parrot: {
    label: 'Parrot',
    description: 'Chatty and colourful. Flaps with joy when happy.',
    price: 700,
    favouriteFood: 'seeds',
    variants: [
      { id: 'green', label: 'Green' },
      { id: 'blue', label: 'Blue & Gold' },
      { id: 'scarlet', label: 'Scarlet' },
    ],
  },
  rabbit: {
    label: 'Rabbit',
    description: 'Soft ears, twitchy nose, and a serious carrot habit.',
    price: 450,
    favouriteFood: 'carrot',
    variants: [
      { id: 'white', label: 'Snow White' },
      { id: 'brown', label: 'Cocoa' },
      { id: 'spotted', label: 'Spotted' },
    ],
  },
  hamster: {
    label: 'Hamster',
    description: 'Tiny, round, and stores snacks in its cheeks.',
    price: 300,
    favouriteFood: 'seeds',
    variants: [
      { id: 'golden', label: 'Golden' },
      { id: 'grey', label: 'Grey' },
      { id: 'panda', label: 'Panda' },
    ],
  },
  tortoise: {
    label: 'Tortoise',
    description: 'Slow and steady. Lives for leafy greens and sunshine.',
    price: 500,
    favouriteFood: 'carrot',
    variants: [
      { id: 'green', label: 'Garden' },
      { id: 'star', label: 'Star' },
      { id: 'desert', label: 'Desert' },
    ],
  },
  otter: {
    label: 'Otter',
    description: "Singapore's most famous residents, straight from Bishan Park. Playful and loud.",
    price: 1500,
    favouriteFood: 'fish',
    variants: [
      { id: 'bishan', label: 'Bishan' },
      { id: 'marina', label: 'Marina' },
    ],
  },
});

export function isPetVariant(species: PetSpecies, variant: string): boolean {
  return PET_SPECIES_INFO[species].variants.some((v) => v.id === variant);
}

/* -------------------------------------------------------------------------- */
/* Supplies                                                                   */
/* -------------------------------------------------------------------------- */

export const PET_SUPPLIES = ['kibble', 'fish', 'seeds', 'carrot', 'toy', 'medicine'] as const;
export type PetSupply = (typeof PET_SUPPLIES)[number];

export const PET_SUPPLY_INFO: Readonly<Record<PetSupply, { label: string; price: number; description: string }>> =
  Object.freeze({
    kibble: { label: 'Kibble', price: 10, description: 'Crunchy and filling. Dogs love it.' },
    fish: { label: 'Ikan Bilis & Fish', price: 15, description: 'A fishy feast. Cats and otters go wild.' },
    seeds: { label: 'Seed Mix', price: 10, description: 'Sunflower seeds and millet for birds and hamsters.' },
    carrot: { label: 'Carrots & Greens', price: 10, description: 'Crunchy veg for rabbits and tortoises.' },
    toy: { label: 'Squeaky Toy', price: 25, description: 'Makes playtime extra fun.' },
    medicine: { label: 'Pet Medicine', price: 40, description: 'Cures a sick pet anywhere, anytime.' },
  });

/** How much each food fills; fish is extra filling. */
export const FOOD_VALUE: Readonly<Record<PetFood, number>> = Object.freeze({
  kibble: 35,
  fish: 45,
  seeds: 35,
  carrot: 35,
});

/* -------------------------------------------------------------------------- */
/* Care numbers                                                               */
/* -------------------------------------------------------------------------- */

export const MAX_PETS = 3;
export const MAX_PET_NAME_LENGTH = 16;
export const VET_PRICE = 50;
export const PET_STAT_MAX = 100;

export interface PetStats {
  food: number;
  fun: number;
  clean: number;
  energy: number;
}

/**
 * Where the pet is decides how its needs change:
 * - out: walking with its owner (hungrier, tires out);
 * - home: resting while its owner is online (bored, recovers energy);
 * - offline: owner away (everything slows right down; energy recovers).
 */
export type PetCareMode = 'out' | 'home' | 'offline';

/** Change per hour, per mode. Negative drains, positive restores. */
export const PET_RATES_PER_HOUR: Readonly<Record<PetCareMode, PetStats>> = Object.freeze({
  out: { food: -10, fun: -4, clean: -8, energy: -12 },
  home: { food: -8, fun: -10, clean: -3, energy: 20 },
  offline: { food: -2, fun: -2, clean: -1, energy: 15 },
});

/** A need is urgent below this. */
export const NEED_THRESHOLD = 30;
export const TIRED_THRESHOLD = 25;

/** All of food, fun and clean at 0 for this long makes a pet sick (never dead). */
export const SICK_AFTER_MS = 3 * 24 * 60 * 60 * 1000;

const HOUR_MS = 60 * 60 * 1000;

const clamp = (value: number) => Math.max(0, Math.min(PET_STAT_MAX, value));

/** Stats after `elapsedMs` in one mode. Pure; the server persists the result. */
export function decayStats(stats: PetStats, elapsedMs: number, mode: PetCareMode): PetStats {
  const hours = Math.max(0, elapsedMs) / HOUR_MS;
  const rate = PET_RATES_PER_HOUR[mode];
  return {
    food: clamp(stats.food + rate.food * hours),
    fun: clamp(stats.fun + rate.fun * hours),
    clean: clamp(stats.clean + rate.clean * hours),
    energy: clamp(stats.energy + rate.energy * hours),
  };
}

/**
 * When, within `elapsedMs` of `stats`, food, fun and clean are ALL empty
 * (ms from the start), or null if that never happens in the window.
 */
export function allEmptyAfter(stats: PetStats, elapsedMs: number, mode: PetCareMode): number | null {
  const rate = PET_RATES_PER_HOUR[mode];
  let latest = 0;
  for (const key of ['food', 'fun', 'clean'] as const) {
    if (stats[key] <= 0) continue;
    if (rate[key] >= 0) return null;
    latest = Math.max(latest, (stats[key] / -rate[key]) * HOUR_MS);
  }
  return latest <= elapsedMs ? latest : null;
}

export type PetMood = 'happy' | 'ok' | 'sad' | 'sleepy' | 'sick';

export interface PetNeeds {
  hungry: boolean;
  bored: boolean;
  dirty: boolean;
  tired: boolean;
}

export function petNeeds(stats: PetStats): PetNeeds {
  return {
    hungry: stats.food < NEED_THRESHOLD,
    bored: stats.fun < NEED_THRESHOLD,
    dirty: stats.clean < NEED_THRESHOLD,
    tired: stats.energy < TIRED_THRESHOLD,
  };
}

export function petMood(stats: PetStats, sick: boolean): PetMood {
  if (sick) return 'sick';
  if (stats.energy < 10) return 'sleepy';
  const care = (stats.food + stats.fun + stats.clean) / 3;
  if (care < NEED_THRESHOLD || stats.food < 10 || stats.fun < 10) return 'sad';
  if (care < 60) return 'ok';
  return 'happy';
}

/** Only content pets come out for a walk. */
export function canGoOut(mood: PetMood): boolean {
  return mood === 'happy' || mood === 'ok';
}

/** The single most pressing need, for status chips. */
export function urgentNeed(needs: PetNeeds, sick: boolean): 'sick' | keyof PetNeeds | null {
  if (sick) return 'sick';
  if (needs.hungry) return 'hungry';
  if (needs.tired) return 'tired';
  if (needs.dirty) return 'dirty';
  if (needs.bored) return 'bored';
  return null;
}

/* -------------------------------------------------------------------------- */
/* Growing up                                                                 */
/* -------------------------------------------------------------------------- */

/** XP needed to reach each level (index 0 = level 1). */
export const PET_LEVEL_XP = [0, 30, 80, 150, 250, 380, 550, 760, 1000, 1300] as const;
export const PET_MAX_LEVEL = PET_LEVEL_XP.length;

export function petLevel(xp: number): number {
  let level = 1;
  for (let i = 0; i < PET_LEVEL_XP.length; i += 1) if (xp >= PET_LEVEL_XP[i]!) level = i + 1;
  return level;
}

export function petNextLevelXp(level: number): number | null {
  return level >= PET_MAX_LEVEL ? null : PET_LEVEL_XP[level]!;
}

export const PET_TRICKS = ['sit', 'spin', 'roll', 'highfive', 'dance'] as const;
export type PetTrick = (typeof PET_TRICKS)[number];

export const PET_TRICK_INFO: Readonly<Record<PetTrick, { label: string; level: number }>> = Object.freeze({
  sit: { label: 'Sit', level: 1 },
  spin: { label: 'Spin', level: 2 },
  roll: { label: 'Roll over', level: 4 },
  highfive: { label: 'High five', level: 6 },
  dance: { label: 'Dance', level: 8 },
});

export function petTricks(level: number): PetTrick[] {
  return PET_TRICKS.filter((trick) => PET_TRICK_INFO[trick].level <= level);
}

export const PET_STAGES = ['baby', 'young', 'adult'] as const;
export type PetStage = (typeof PET_STAGES)[number];

/** Days (with at least one care action) to grow up. */
export const YOUNG_AFTER_CARE_DAYS = 3;
export const ADULT_AFTER_CARE_DAYS = 10;

export function petStage(careDays: number): PetStage {
  if (careDays >= ADULT_AFTER_CARE_DAYS) return 'adult';
  if (careDays >= YOUNG_AFTER_CARE_DAYS) return 'young';
  return 'baby';
}

/** Care days after which a pet has grown as big as it will get. */
export const PET_MAX_GROWTH = 40;

/**
 * How big a pet has grown, 0 (just adopted) to PET_MAX_GROWTH: one step for
 * every day it was cared for, so a pet keeps getting bigger for over a month
 * (a baby is small, an adult is full size at day 10, and it keeps growing to
 * about a third bigger than that).
 */
export function petGrowth(careDays: number): number {
  return Math.max(0, Math.min(PET_MAX_GROWTH, Math.floor(careDays)));
}

/** The growth a pet of a stage has at least reached (used where only the stage is known). */
export const STAGE_GROWTH: Readonly<Record<PetStage, number>> = Object.freeze({
  baby: 0,
  young: YOUNG_AFTER_CARE_DAYS,
  adult: ADULT_AFTER_CARE_DAYS,
});

const DAY_MS = 24 * 60 * 60 * 1000;
/** Pet ages and birthdays follow the Singapore calendar day. */
const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;

export function petAgeDays(adoptedAt: number, now: number): number {
  const day = (t: number) => Math.floor((t + SGT_OFFSET_MS) / DAY_MS);
  return Math.max(0, day(now) - day(adoptedAt));
}

/** Milestone days that get a party, plus every yearly anniversary. */
const PARTY_DAYS = new Set([7, 30, 100, 200]);

export function isPetBirthday(adoptedAt: number, now: number): boolean {
  const age = petAgeDays(adoptedAt, now);
  if (age === 0) return false;
  if (PARTY_DAYS.has(age)) return true;
  const a = new Date(adoptedAt + SGT_OFFSET_MS);
  const n = new Date(now + SGT_OFFSET_MS);
  return a.getUTCMonth() === n.getUTCMonth() && a.getUTCDate() === n.getUTCDate();
}

/* -------------------------------------------------------------------------- */
/* Digging                                                                    */
/* -------------------------------------------------------------------------- */

export const DIG_COOLDOWN_MS = 2 * 60 * 1000;
/** Digging income per player per day (Singapore day). */
export const DIG_DAILY_CAP = 150;

export const digRewardSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('money'), amount: z.number().int(), label: z.string() }),
  z.object({ kind: z.literal('supply'), supply: z.enum(PET_SUPPLIES) }),
  z.object({ kind: z.literal('furniture'), furnitureKind: z.string() }),
  z.object({ kind: z.literal('nothing'), label: z.string() }),
]);
export type DigReward = z.infer<typeof digRewardSchema>;

/* -------------------------------------------------------------------------- */
/* Wire shapes                                                                */
/* -------------------------------------------------------------------------- */

export const PET_CARE_ACTIONS = ['feed', 'play', 'bath', 'nap', 'water', 'medicine'] as const;
export type PetCareAction = (typeof PET_CARE_ACTIONS)[number];

const speciesSchema = z.enum(PET_SPECIES);
const moodSchema = z.enum(['happy', 'ok', 'sad', 'sleepy', 'sick']);
const needsSchema = z.object({ hungry: z.boolean(), bored: z.boolean(), dirty: z.boolean(), tired: z.boolean() });

/** A tile position, duplicated locally to avoid importing from messages.ts (which imports this file). */
const petTileSchema = z.object({ x: z.number().int(), y: z.number().int() });
const petDirectionSchema = z.number().int().min(0).max(7);

/** What everyone in a room knows about a pet that is out. */
export const roomPetSchema = z.object({
  ownerId: z.string(),
  petId: z.string(),
  name: z.string(),
  species: speciesSchema,
  variant: z.string(),
  stage: z.enum(PET_STAGES),
  /** 0 to PET_MAX_GROWTH: how big it has grown. */
  growth: z.number().int().min(0).max(PET_MAX_GROWTH),
  level: z.number().int(),
  mood: moodSchema,
  needs: needsSchema,
  birthday: z.boolean(),
  /** False (the default): the pet just trails its owner, client-side, as before. */
  roaming: z.boolean(),
  /** Parrots only: true to ride on the owner's shoulder instead of flying alongside. */
  perch: z.boolean(),
  /** Server position, present only while `roaming` is true. */
  x: z.number().optional(),
  y: z.number().optional(),
  z: z.number().optional(),
  direction: petDirectionSchema.optional(),
});
export type RoomPet = z.infer<typeof roomPetSchema>;

export const petDiaryEntrySchema = z.object({ kind: z.string(), text: z.string(), at: z.number() });
export type PetDiaryEntry = z.infer<typeof petDiaryEntrySchema>;

/** Everything an owner sees about their own pet. */
export const petViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  species: speciesSchema,
  variant: z.string(),
  stage: z.enum(PET_STAGES),
  growth: z.number().int().min(0).max(PET_MAX_GROWTH),
  stats: z.object({ food: z.number(), fun: z.number(), clean: z.number(), energy: z.number() }),
  /** Server time the stats were computed, so the client can keep decaying them. */
  statsAt: z.number(),
  mode: z.enum(['out', 'home', 'offline']),
  mood: moodSchema,
  needs: needsSchema,
  sick: z.boolean(),
  xp: z.number().int(),
  level: z.number().int(),
  nextLevelXp: z.number().int().nullable(),
  tricks: z.array(z.enum(PET_TRICKS)),
  ageDays: z.number().int(),
  careDays: z.number().int(),
  birthday: z.boolean(),
  out: z.boolean(),
  /** True while out and roaming the room on its own, instead of following you. */
  roaming: z.boolean(),
  /** Parrots only: true to ride on the owner's shoulder instead of flying alongside. */
  perch: z.boolean(),
  adoptedAt: z.number(),
  digReadyAt: z.number(),
  diary: z.array(petDiaryEntrySchema),
  /** True to stop the "is hungry"/"is bored" reminders for this pet, in chat and the status chip. */
  remindersMuted: z.boolean(),
});
export type PetView = z.infer<typeof petViewSchema>;

export const petSuppliesSchema = z.record(z.enum(PET_SUPPLIES), z.number().int());
export type PetSupplies = Partial<Record<PetSupply, number>>;

const petId = z.string().min(1).max(64);

export const petClientMessageSchema = z.discriminatedUnion('t', [
  /** Ask for your pets and supplies (answered with pet:list). */
  z.object({ t: z.literal('pet:sync') }),
  /** Walk with a pet (or put it away with petId null). */
  z.object({ t: z.literal('pet:out'), petId: petId.nullable() }),
  z.object({ t: z.literal('pet:care'), petId, action: z.enum(PET_CARE_ACTIONS), food: z.enum(PET_FOODS).optional() }),
  z.object({ t: z.literal('pet:trick'), petId, trick: z.enum(PET_TRICKS) }),
  z.object({ t: z.literal('pet:dig'), petId }),
  /** Follow the owner (false, the default) or roam the room on its own (true). */
  z.object({ t: z.literal('pet:roam'), petId, roam: z.boolean() }),
  /** Parrots only: ride on the owner's shoulder (true) or fly alongside (false, the default). */
  z.object({ t: z.literal('pet:perch'), petId, perch: z.boolean() }),
  /** Send a roaming pet on a fast lap at the Bishan Dog Run. */
  z.object({ t: z.literal('pet:race'), petId }),
]);

export const petServerMessageSchema = z.discriminatedUnion('t', [
  /** Your pets and supplies. Sent to the owner only. */
  z.object({
    t: z.literal('pet:list'),
    pets: z.array(petViewSchema),
    supplies: petSuppliesSchema,
    serverTime: z.number(),
  }),
  /** Every pet out in the room you just joined. */
  z.object({ t: z.literal('pet:room'), pets: z.array(roomPetSchema) }),
  /** A pet came out, or its mood/needs changed. */
  z.object({ t: z.literal('pet:appear'), pet: roomPetSchema }),
  z.object({ t: z.literal('pet:gone'), ownerId: z.string() }),
  /** Show an animation on someone's pet. */
  z.object({
    t: z.literal('pet:anim'),
    ownerId: z.string(),
    anim: z.enum([
      'sit',
      'spin',
      'roll',
      'highfive',
      'dance',
      'eat',
      'play',
      'bath',
      'nap',
      'dig',
      'cured',
      'levelup',
      'greet',
    ]),
  }),
  /** Treasure! Sent to the owner (others see the dig animation). */
  z.object({ t: z.literal('pet:dug'), petId: z.string(), reward: digRewardSchema }),
  z.object({ t: z.literal('pet:changed') }),
  /** A roaming pet's committed route, mirrors avatar:path. Only sent while roaming. */
  z.object({
    t: z.literal('pet:path'),
    petId: z.string(),
    ownerId: z.string(),
    from: z.object({ x: z.number(), y: z.number(), z: z.number() }),
    path: z.array(petTileSchema),
    startedAt: z.number(),
    /** Ms per tile; defaults to the normal walking pace when omitted (a race lap is faster). */
    stepMs: z.number().optional(),
  }),
]);

export type PetClientMessage = z.infer<typeof petClientMessageSchema>;
export type PetServerMessage = z.infer<typeof petServerMessageSchema>;

/* -------------------------------------------------------------------------- */
/* HTTP shapes                                                                */
/* -------------------------------------------------------------------------- */

export interface AdoptRequest {
  species: PetSpecies;
  variant: string;
  name: string;
}

export interface BuySuppliesRequest {
  supply: PetSupply;
  qty: number;
}
